/**
 * Pretext Canvas Renderer — Two-Layer Architecture
 *
 * Layer 1 (base canvas): All text at full brightness. Only redrawn on
 * scroll, layout, or settings changes — NOT on gaze movement.
 *
 * Layer 2 (overlay canvas): Semi-transparent dark overlay with a transparent
 * "spotlight" window around the focused line. Redrawn on gaze movement
 * but doesn't touch the text at all.
 *
 * This eliminates text flicker entirely because text pixels are never
 * cleared/redrawn during normal gaze-driven reading.
 */

import { prepareWithSegments, layoutWithLines } from '@chenglou/pretext'
import type { Paragraph, RenderedLine, ReadingSettings, GazeState } from '@/types'

// Cache prepared text to avoid re-measuring on every frame
const preparedCache = new Map<string, ReturnType<typeof prepareWithSegments>>()

function getPrepared(text: string, font: string) {
  const key = `${font}::${text}`
  let prepared = preparedCache.get(key)
  if (!prepared) {
    prepared = prepareWithSegments(text, font)
    preparedCache.set(key, prepared)
  }
  return prepared
}

export function clearPreparedCache() {
  preparedCache.clear()
}

/**
 * Compute the full layout for a document's paragraphs.
 */
export function computeLayout(
  paragraphs: Paragraph[],
  settings: ReadingSettings,
  canvasWidth: number,
): { lines: RenderedLine[]; totalHeight: number } {
  const lines: RenderedLine[] = []
  const marginX = Math.max(40, (canvasWidth - settings.columnWidth) / 2)
  const maxWidth = Math.min(settings.columnWidth, canvasWidth - marginX * 2)
  let y = 60
  let globalLineIndex = 0

  for (let pi = 0; pi < paragraphs.length; pi++) {
    const para = paragraphs[pi]!
    const fontSize = para.type === 'heading' ? settings.fontSize * 1.5 : settings.fontSize
    const lineHeight = para.type === 'heading' ? settings.lineHeight * 1.3 : settings.lineHeight
    const fontWeight = para.type === 'heading' ? 'bold' : 'normal'
    const font = `${fontWeight} ${fontSize}px ${settings.fontFamily}`

    const prepared = getPrepared(para.text, font)
    const { lines: layoutLines } = layoutWithLines(prepared, maxWidth, lineHeight)

    for (let li = 0; li < layoutLines.length; li++) {
      const line = layoutLines[li]!
      lines.push({
        text: line.text,
        x: marginX,
        y,
        width: line.width,
        font,
        lineHeight,
        paragraphIndex: pi,
        lineIndexInParagraph: li,
        globalLineIndex,
      })
      y += lineHeight
      globalLineIndex++
    }

    y += para.type === 'heading' ? lineHeight * 0.5 : lineHeight * 0.4
  }

  return { lines, totalHeight: y }
}

/**
 * Render all text to the base canvas. Called only when scroll/layout/settings change.
 */
export function renderBaseLayer(
  ctx: CanvasRenderingContext2D,
  lines: RenderedLine[],
  settings: ReadingSettings,
  scrollY: number,
  canvasWidth: number,
  canvasHeight: number,
) {
  const bg = settings.theme === 'dark' ? '#0f172a'
    : settings.theme === 'sepia' ? '#f5f0e8'
    : '#ffffff'
  ctx.fillStyle = bg
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  const textColor = settings.theme === 'dark' ? '#e2e8f0'
    : settings.theme === 'sepia' ? '#3d3929'
    : '#1e293b'

  for (const line of lines) {
    const screenY = line.y - scrollY
    if (screenY < -50 || screenY > canvasHeight + 50) continue

    ctx.font = line.font
    ctx.textBaseline = 'top'
    ctx.fillStyle = textColor
    ctx.fillText(line.text, line.x, screenY)
  }
}

/**
 * Smooth spotlight position for the overlay.
 */
let _smoothSpotY: number | null = null
const SPOTLIGHT_LERP = 0.4 // faster transition when line changes

/**
 * Per-line scale state for smooth font size transitions.
 * Focused line scales up, adjacent lines scale slightly, others stay at 1.0.
 */
const lineScale: Map<number, number> = new Map()
const SCALE_LERP = 0.3 // faster text scale animation

const FOCUS_SCALE = 1.4     // focused line: 40% larger
const ADJACENT_SCALE = 1.15 // ±1 lines: 15% larger
const NORMAL_SCALE = 1.0    // all other lines

/**
 * Parse a CSS font string to extract weight, size, and family.
 */
function parseFont(font: string): { weight: string; size: number; family: string } {
  // e.g. "bold 27px Inter" or "normal 18px Inter"
  const match = font.match(/^(bold|normal)\s+([\d.]+)px\s+(.+)$/)
  if (match) {
    return { weight: match[1]!, size: parseFloat(match[2]!), family: match[3]! }
  }
  return { weight: 'normal', size: 18, family: 'Inter' }
}

/**
 * Build a CSS font string with a different size.
 */
function buildFont(weight: string, size: number, family: string): string {
  return `${weight} ${size}px ${family}`
}

/**
 * Render the gaze overlay with dynamic text scaling.
 *
 * 1. Spotlight dimming (semi-transparent overlay)
 * 2. Enlarged focused text (Pretext re-measured at larger font size)
 *
 * The focused line(s) are re-rendered on the overlay at a larger size,
 * covering the base canvas text underneath. Pretext's <0.1ms layout
 * makes per-frame re-measurement trivially fast.
 */
export function renderOverlay(
  ctx: CanvasRenderingContext2D,
  lines: RenderedLine[],
  settings: ReadingSettings,
  gazeState: GazeState,
  scrollY: number,
  canvasWidth: number,
  canvasHeight: number,
) {
  // Clear overlay (fully transparent)
  ctx.clearRect(0, 0, canvasWidth, canvasHeight)

  if (!settings.gazeHighlightEnabled || gazeState.fixationLine === null) {
    // Smoothly scale all lines back to 1.0 when gaze is inactive
    let needsRedraw = false
    for (const [idx, scale] of lineScale) {
      if (Math.abs(scale - NORMAL_SCALE) > 0.005) {
        lineScale.set(idx, scale + (NORMAL_SCALE - scale) * SCALE_LERP)
        needsRedraw = true
      } else {
        lineScale.delete(idx)
      }
    }

    // If lines are still animating back, render them
    if (needsRedraw) {
      const bg = settings.theme === 'dark' ? '#0f172a'
        : settings.theme === 'sepia' ? '#f5f0e8'
        : '#ffffff'
      ctx.fillStyle = bg
      ctx.fillRect(0, 0, canvasWidth, canvasHeight)
      renderAllLines(ctx, lines, settings, scrollY, canvasWidth, canvasHeight, null, 0)
    }

    _smoothSpotY = null
    return
  }

  // Find the focused line's screen position
  const focusedLine = lines.find(l => l.globalLineIndex === gazeState.fixationLine)
  if (!focusedLine) return

  const targetY = focusedLine.y - scrollY + focusedLine.lineHeight * 0.5

  // Smooth the spotlight position
  if (_smoothSpotY === null) {
    _smoothSpotY = targetY
  } else {
    _smoothSpotY += (targetY - _smoothSpotY) * SPOTLIGHT_LERP
  }
  const spotY = _smoothSpotY

  const bg = settings.theme === 'dark' ? '#0f172a'
    : settings.theme === 'sepia' ? '#f5f0e8'
    : '#ffffff'

  // Fully opaque overlay: background + all text rendered per-line.
  // No transparency = no visible seams. Dimming is applied per-line via color blending.
  ctx.fillStyle = bg
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  // --- Dynamic text scaling + per-line dimming ---
  for (const line of lines) {
    const screenY = line.y - scrollY
    if (screenY < -50 || screenY > canvasHeight + 50) continue

    const dist = Math.abs(line.globalLineIndex - gazeState.fixationLine)
    let targetScale = NORMAL_SCALE
    if (dist === 0) targetScale = FOCUS_SCALE
    else if (dist === 1) targetScale = ADJACENT_SCALE

    const currentScale = lineScale.get(line.globalLineIndex) ?? NORMAL_SCALE
    const newScale = currentScale + (targetScale - currentScale) * SCALE_LERP
    lineScale.set(line.globalLineIndex, newScale)
  }

  // Render all visible lines with per-line brightness based on distance from focus
  renderAllLines(ctx, lines, settings, scrollY, canvasWidth, canvasHeight, gazeState.fixationLine, spotY)
}

/**
 * Hex color helpers for per-line brightness blending.
 */
function hexToRgb(hex: string): [number, number, number] {
  return [parseInt(hex.slice(1,3),16), parseInt(hex.slice(3,5),16), parseInt(hex.slice(5,7),16)]
}
function blendRgb(a: [number,number,number], b: [number,number,number], t: number): string {
  return `rgb(${Math.round(a[0]+(b[0]-a[0])*t)},${Math.round(a[1]+(b[1]-a[1])*t)},${Math.round(a[2]+(b[2]-a[2])*t)})`
}

/**
 * Per-line brightness for smooth dimming transitions.
 */
const lineBrightness: Map<number, number> = new Map()
const BRIGHT_LERP = 0.2

/**
 * Render all visible lines on the overlay with per-line dimming and scaling.
 * The overlay is fully opaque (no transparency = no seams).
 */
function renderAllLines(
  ctx: CanvasRenderingContext2D,
  lines: RenderedLine[],
  settings: ReadingSettings,
  scrollY: number,
  canvasWidth: number,
  canvasHeight: number,
  fixationLine: number | null,
  spotY: number,
) {
  const textColorHex = settings.theme === 'dark' ? '#e2e8f0'
    : settings.theme === 'sepia' ? '#3d3929'
    : '#1e293b'
  const dimColorHex = settings.theme === 'dark' ? '#334155'
    : settings.theme === 'sepia' ? '#9e9684'
    : '#94a3b8'
  const highlightHex = settings.theme === 'dark' ? '#f0f9ff'
    : settings.theme === 'sepia' ? '#1c1b18'
    : '#0f172a'

  const textRgb = hexToRgb(textColorHex)
  const dimRgb = hexToRgb(dimColorHex)
  const highlightRgb = hexToRgb(highlightHex)

  for (const line of lines) {
    const screenY = line.y - scrollY
    if (screenY < -50 || screenY > canvasHeight + 50) continue

    const scale = lineScale.get(line.globalLineIndex) ?? NORMAL_SCALE
    const isFocused = fixationLine !== null && line.globalLineIndex === fixationLine
    const dist = fixationLine !== null ? Math.abs(line.globalLineIndex - fixationLine) : 999

    // Target brightness: 0 = dim, 1 = normal/adjacent, 2 = focused
    let targetBright = 0
    if (dist === 0) targetBright = 2
    else if (dist === 1) targetBright = 1
    else if (dist <= 3) targetBright = 0.4
    else targetBright = 0

    const currentBright = lineBrightness.get(line.globalLineIndex) ?? targetBright
    const bright = currentBright + (targetBright - currentBright) * BRIGHT_LERP
    lineBrightness.set(line.globalLineIndex, bright)

    // Compute text color from brightness
    let color: string
    if (bright >= 1) {
      // Blend text → highlight
      const t = Math.min(1, (bright - 1))
      color = blendRgb(textRgb, highlightRgb, t)
    } else {
      // Blend dim → text
      color = blendRgb(dimRgb, textRgb, Math.max(0, bright))
    }

    if (Math.abs(scale - NORMAL_SCALE) < 0.005) {
      // Normal size
      ctx.font = line.font
      ctx.textBaseline = 'top'
      ctx.fillStyle = color
      ctx.fillText(line.text, line.x, screenY)
    } else {
      // Scaled size via Pretext
      const parsed = parseFont(line.font)
      const scaledFontSize = parsed.size * scale
      const scaledLineHeight = line.lineHeight * scale
      const scaledFont = buildFont(parsed.weight, scaledFontSize, parsed.family)

      const prepared = getPrepared(line.text, scaledFont)
      const { lines: scaledLines } = layoutWithLines(prepared, canvasWidth - line.x * 2, scaledLineHeight)
      const scaledText = scaledLines[0]?.text ?? line.text

      const yOffset = (scaledLineHeight - line.lineHeight) * 0.5

      ctx.font = scaledFont
      ctx.textBaseline = 'top'
      ctx.fillStyle = color
      ctx.fillText(scaledText, line.x, screenY - yOffset)
    }
  }
}

/**
 * Find which line the gaze is targeting (nearest-line matching).
 */
export function gazeToLine(
  gazeY: number,
  scrollY: number,
  lines: RenderedLine[],
): number | null {
  if (lines.length === 0) return null

  const documentY = gazeY + scrollY
  let bestLine: number | null = null
  let bestDist = Infinity

  for (const line of lines) {
    const lineCenter = line.y + line.lineHeight * 0.35
    const dist = Math.abs(documentY - lineCenter)
    if (dist < bestDist) {
      bestDist = dist
      bestLine = line.globalLineIndex
    }
  }

  if (bestLine !== null) {
    const refLine = lines.find(l => l.globalLineIndex === bestLine)
    if (refLine && bestDist > refLine.lineHeight * 5) {
      return null
    }
  }

  return bestLine
}
